/* ==========================================================================
   Planetry Prints · design system behaviour
   --------------------------------------------------------------------------
   Header that takes the edition of the section beneath it, scroll reveals,
   the Date Field (popover calendar on desktop, native picker on phones),
   menu sheet, search overlay, film modal, video pause controls and rail
   progress lines. Exposes window.PP for page sections.
   ========================================================================== */
(function () {
  'use strict';

  var doc = document;
  var root = doc.documentElement;
  root.classList.add('pp-js');

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  var DOW = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  function pad(n) { return n < 10 ? '0' + n : String(n); }
  function iso(y, m, d) { return y + '-' + pad(m) + '-' + pad(d); }
  function daysIn(y, m) { return new Date(Date.UTC(y, m, 0)).getUTCDate(); }
  function parse(s) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s || '');
    if (!m) return null;
    var y = +m[1], mo = +m[2], d = +m[3];
    if (mo < 1 || mo > 12 || d < 1 || d > daysIn(y, mo)) return null;
    return { y: y, m: mo, d: d };
  }
  function todayISO() {
    var t = new Date();
    return iso(t.getFullYear(), t.getMonth() + 1, t.getDate());
  }
  function formatDate(s, short) {
    var p = parse(s);
    if (!p) return '';
    var month = MONTHS[p.m - 1];
    return p.d + ' ' + (short ? month.slice(0, 3) : month) + ' ' + p.y;
  }
  /* Studios already read ?pp_date= (and the print ?pp_message=). */
  function studioUrl(kind, date, extra) {
    var base = kind === 'case' ? '/products/sky-code-personalized-phone-case' : '/products/custom-planetry-art-print';
    var q = [];
    if (date) q.push('pp_date=' + encodeURIComponent(date));
    for (var k in (extra || {})) if (extra[k]) q.push(k + '=' + encodeURIComponent(extra[k]));
    return base + (q.length ? '?' + q.join('&') : '');
  }
  function randomDate() {
    /* Surprise me: a random day from the last 75 years. */
    var now = Date.now();
    var t = now - Math.random() * 75 * 365.25 * 86400000;
    var d = new Date(t);
    return iso(d.getFullYear(), d.getMonth() + 1, d.getDate());
  }

  var PP = window.PP = window.PP || {};
  PP.MONTHS = MONTHS;
  PP.parse = parse;
  PP.iso = iso;
  PP.todayISO = todayISO;
  PP.formatDate = formatDate;
  PP.studioUrl = studioUrl;
  PP.randomDate = randomDate;
  PP.reduceMotion = reduceMotion;

  /* ---- reveals ------------------------------------------------------------ */
  var revealObserver = null;
  function observeReveals(scope) {
    var items = (scope || doc).querySelectorAll('[data-pp-reveal]:not(.is-in), [data-pp-draw]:not(.is-in)');
    if (!items.length) return;
    if (!('IntersectionObserver' in window) || reduceMotion.matches) {
      items.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (e) {
          if (e.isIntersecting) {
            e.target.classList.add('is-in');
            revealObserver.unobserve(e.target);
          }
        });
      }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    }
    items.forEach(function (n) { revealObserver.observe(n); });
  }
  PP.observeReveals = observeReveals;

  /* ---- header ------------------------------------------------------------- */
  function initHeader() {
    var header = doc.querySelector('[data-pp-header]');
    if (!header) return;
    var wrapper = header.closest('.shopify-section') || header;
    var lastY = window.scrollY;
    var ticking = false;
    var mega = header.querySelector('[data-pp-mega]');
    var megaBtn = header.querySelector('[data-pp-mega-toggle]');

    function surfaceAt(y) {
      var x = Math.min(window.innerWidth - 2, 24);
      var stack = doc.elementsFromPoint ? doc.elementsFromPoint(x, y) : [doc.elementFromPoint(x, y)];
      for (var i = 0; i < stack.length; i++) {
        var n = stack[i];
        if (!n || wrapper.contains(n)) continue;
        var s = n.closest('[data-pp-surface]');
        if (s) return s;
        return null;
      }
      return null;
    }

    function update() {
      ticking = false;
      var rect = header.getBoundingClientRect();
      var behind = surfaceAt(Math.max(1, rect.bottom - 2));
      var surface = behind ? behind.getAttribute('data-pp-surface') : 'day';
      if (surface === 'auto') {
        /* Dawn and dusk bands: night above the split, day below it (dusk flips). */
        var br = behind.getBoundingClientRect();
        var frac = (rect.bottom - br.top) / Math.max(1, br.height);
        var split = parseFloat(behind.getAttribute('data-pp-split') || '0.6');
        var upper = behind.getAttribute('data-pp-band') === 'dusk' ? 'day' : 'night';
        surface = frac < split ? upper : (upper === 'night' ? 'day' : 'night');
      }
      var over = !!(behind && behind.hasAttribute('data-pp-underlay') && behind.getBoundingClientRect().top <= rect.top + 1);
      header.setAttribute('data-surface', surface === 'night' ? 'night' : 'day');
      header.setAttribute('data-over', over && surface === 'night' ? 'true' : 'false');

      var y = window.scrollY;
      var open = header.classList.contains('is-mega-open') || doc.body.classList.contains('pp-sheet-open');
      if (!open) {
        if (y > lastY + 4 && y > rect.height * 3) wrapper.classList.add('is-hidden');
        else if (y < lastY - 4 || y < rect.height) wrapper.classList.remove('is-hidden');
      }
      lastY = y;
    }

    function request() {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }
    window.addEventListener('scroll', request, { passive: true });
    window.addEventListener('resize', request);
    doc.addEventListener('pp:surfacechange', request);
    update();
    setTimeout(update, 400);

    /* Shop menu */
    if (mega && megaBtn) {
      var closeTimer = null;
      var setMega = function (open) {
        clearTimeout(closeTimer);
        megaBtn.setAttribute('aria-expanded', open ? 'true' : 'false');
        mega.classList.toggle('is-open', open);
        header.classList.toggle('is-mega-open', open);
        if (open) { header.setAttribute('data-surface', 'day'); header.setAttribute('data-over', 'false'); }
        else request();
      };
      megaBtn.addEventListener('click', function () { setMega(megaBtn.getAttribute('aria-expanded') !== 'true'); });
      if (finePointer.matches) {
        megaBtn.addEventListener('mouseenter', function () { setMega(true); });
        header.addEventListener('mouseleave', function () { closeTimer = setTimeout(function () { setMega(false); }, 180); });
        header.addEventListener('mouseenter', function () { clearTimeout(closeTimer); });
      }
      header.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && megaBtn.getAttribute('aria-expanded') === 'true') { setMega(false); megaBtn.focus(); }
      });
      header.addEventListener('focusout', function (e) {
        if (!header.contains(e.relatedTarget)) setMega(false);
      });
    }
  }

  /* ---- sheets: mobile menu and search overlay -------------------------------- */
  function initSheets() {
    var openSheet = null;
    var opener = null;

    function close() {
      if (!openSheet) return;
      openSheet.classList.remove('is-open');
      openSheet.setAttribute('aria-hidden', 'true');
      doc.body.classList.remove('pp-sheet-open');
      doc.body.style.overflow = '';
      var s = openSheet;
      openSheet = null;
      setTimeout(function () { if (!s.classList.contains('is-open')) s.setAttribute('hidden', ''); }, 320);
      if (opener) opener.focus();
    }

    function open(sheet, from) {
      if (openSheet) close();
      opener = from || null;
      openSheet = sheet;
      sheet.removeAttribute('hidden');
      sheet.setAttribute('aria-hidden', 'false');
      doc.body.classList.add('pp-sheet-open');
      doc.body.style.overflow = 'hidden';
      requestAnimationFrame(function () {
        sheet.classList.add('is-open');
        var focusTarget = sheet.querySelector('[data-pp-autofocus]') || sheet.querySelector('button, a, input');
        if (focusTarget) setTimeout(function () { focusTarget.focus(); }, 60);
      });
    }

    doc.addEventListener('click', function (e) {
      var t = e.target.closest('[data-pp-sheet-open]');
      if (t) {
        var sheet = doc.getElementById(t.getAttribute('data-pp-sheet-open'));
        if (sheet) { e.preventDefault(); open(sheet, t); }
        return;
      }
      if (e.target.closest('[data-pp-sheet-close]')) { e.preventDefault(); close(); }
    });

    doc.addEventListener('keydown', function (e) {
      if (!openSheet) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key === 'Tab') {
        var f = openSheet.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]), [tabindex]:not([tabindex="-1"])');
        f = Array.prototype.filter.call(f, function (n) { return n.offsetParent !== null; });
        if (!f.length) return;
        var first = f[0], last = f[f.length - 1];
        if (e.shiftKey && doc.activeElement === first) { e.preventDefault(); last.focus(); }
        else if (!e.shiftKey && doc.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });

    PP.closeSheet = close;
  }

  /* ---- search ------------------------------------------------------------- */
  function initSearch() {
    var sheet = doc.querySelector('[data-pp-search]');
    if (!sheet) return;
    var input = sheet.querySelector('input[name="q"]');
    var results = sheet.querySelector('[data-pp-search-results]');
    var timer = null, controller = null;

    function run() {
      var q = input.value.trim();
      if (controller) controller.abort();
      if (q.length < 2) { results.innerHTML = ''; return; }
      controller = window.AbortController ? new AbortController() : null;
      var url = (window.Shopify && Shopify.routes ? Shopify.routes.root : '/') +
        'search/suggest?q=' + encodeURIComponent(q) +
        '&section_id=pp-search-results&resources[type]=product,page,article&resources[limit]=8&resources[limit_scope]=each';
      fetch(url, controller ? { signal: controller.signal } : {})
        .then(function (r) { return r.text(); })
        .then(function (html) {
          var tmp = doc.createElement('div');
          tmp.innerHTML = html;
          var inner = tmp.querySelector('[data-pp-search-inner]');
          results.innerHTML = inner ? inner.innerHTML : '';
        })
        .catch(function () {});
    }

    input.addEventListener('input', function () {
      clearTimeout(timer);
      timer = setTimeout(run, 220);
    });

    sheet.querySelectorAll('[data-pp-search-chip]').forEach(function (b) {
      b.addEventListener('click', function () {
        input.value = b.getAttribute('data-pp-search-chip');
        input.focus();
        run();
      });
    });
  }

  /* ---- Date Field ----------------------------------------------------------- */
  var fieldCount = 0;

  function DateField(el) {
    this.el = el;
    this.min = parse(el.getAttribute('data-min') || '1900-01-01');
    this.max = parse(el.getAttribute('data-max') || '2100-12-31');
    this.control = el.querySelector('.pp-datefield__control');
    this.valueEl = el.querySelector('.pp-datefield__value');
    this.native = el.querySelector('.pp-datefield__native');
    this.hidden = el.querySelector('input[type="hidden"]');
    this.prompts = [];
    try { this.prompts = JSON.parse(el.getAttribute('data-prompts') || '[]'); } catch (_) { this.prompts = []; }
    this.promptIndex = 0;
    this.value = '';
    this.view = null;
    this.focusDay = null;
    this.id = 'pp-cal-' + (++fieldCount);
    this.build();
    this.bind();
    var initial = el.getAttribute('data-value');
    if (initial && parse(initial)) this.set(initial, true);
    else this.showPlaceholder();
    this.cycle();
  }

  DateField.prototype.build = function () {
    var cal = doc.createElement('div');
    cal.className = 'pp-cal';
    cal.id = this.id;
    cal.setAttribute('role', 'dialog');
    cal.setAttribute('aria-label', 'Choose a date');
    cal.hidden = true;
    cal.innerHTML =
      '<div class="pp-cal__years" role="listbox" aria-label="Year"></div>' +
      '<div class="pp-cal__main">' +
        '<div class="pp-cal__head">' +
          '<button type="button" class="pp-cal__nav" data-step="-1" aria-label="Previous month">&#8249;</button>' +
          '<span class="pp-cal__month" aria-live="polite"></span>' +
          '<button type="button" class="pp-cal__nav" data-step="1" aria-label="Next month">&#8250;</button>' +
        '</div>' +
        '<div class="pp-cal__grid" role="grid"></div>' +
        '<div class="pp-cal__foot"><button type="button" data-today>Today</button><button type="button" data-close>Close</button></div>' +
      '</div>';
    this.el.appendChild(cal);
    this.cal = cal;
    this.yearsEl = cal.querySelector('.pp-cal__years');
    this.monthEl = cal.querySelector('.pp-cal__month');
    this.gridEl = cal.querySelector('.pp-cal__grid');
    this.control.setAttribute('aria-controls', this.id);
    this.control.setAttribute('aria-expanded', 'false');
    var frag = doc.createDocumentFragment();
    for (var y = this.max.y; y >= this.min.y; y--) {
      var b = doc.createElement('button');
      b.type = 'button';
      b.className = 'pp-cal__year';
      b.setAttribute('role', 'option');
      b.setAttribute('data-year', y);
      b.textContent = y;
      frag.appendChild(b);
    }
    this.yearsEl.appendChild(frag);
    if (this.native) {
      this.native.min = iso(this.min.y, this.min.m, this.min.d);
      this.native.max = iso(this.max.y, this.max.m, this.max.d);
    }
  };

  DateField.prototype.bind = function () {
    var self = this;
    this.control.addEventListener('click', function () {
      if (!finePointer.matches && self.native) {
        /* Assistive tech activates the button rather than the overlaid input. */
        try { self.native.showPicker(); } catch (_) { self.native.focus(); self.native.click(); }
        return;
      }
      self.toggle();
    });
    this.control.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') { e.preventDefault(); self.open(); }
    });
    if (this.native) {
      this.native.addEventListener('change', function () {
        if (parse(self.native.value)) self.set(self.native.value);
      });
      this.native.addEventListener('focus', function () { self.el.classList.add('is-open'); });
      this.native.addEventListener('blur', function () { self.el.classList.remove('is-open'); });
    }
    this.cal.addEventListener('click', function (e) {
      var day = e.target.closest('[data-date]');
      if (day) { self.set(day.getAttribute('data-date')); self.close(true); return; }
      var year = e.target.closest('[data-year]');
      if (year) { self.setView(+year.getAttribute('data-year'), self.view.m, true); return; }
      var step = e.target.closest('[data-step]');
      if (step) { self.stepMonth(+step.getAttribute('data-step')); return; }
      if (e.target.closest('[data-today]')) { self.set(todayISO()); self.close(true); return; }
      if (e.target.closest('[data-close]')) self.close(true);
    });
    this.gridEl.addEventListener('keydown', function (e) { self.gridKey(e); });
    this.cal.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); self.close(true); }
    });
    doc.addEventListener('pointerdown', function (e) {
      if (self.el.classList.contains('is-open') && !self.el.contains(e.target)) self.close(false);
    });
    this.el.addEventListener('focusout', function (e) {
      if (e.relatedTarget && !self.el.contains(e.relatedTarget)) self.close(false);
    });
  };

  DateField.prototype.showPlaceholder = function () {
    var text = this.prompts[this.promptIndex % Math.max(1, this.prompts.length)] || this.el.getAttribute('data-placeholder') || 'Choose a date';
    this.valueEl.innerHTML = '';
    var span = doc.createElement('span');
    span.className = 'pp-datefield__placeholder';
    span.textContent = text;
    this.valueEl.appendChild(span);
  };

  DateField.prototype.cycle = function () {
    var self = this;
    if (this.prompts.length < 2) return;
    setInterval(function () {
      if (self.value || reduceMotion.matches || self.el.classList.contains('is-open') || doc.hidden) return;
      var ph = self.valueEl.querySelector('.pp-datefield__placeholder');
      if (!ph) return;
      ph.classList.add('is-leaving');
      setTimeout(function () {
        if (self.value) return;
        self.promptIndex++;
        ph.textContent = self.prompts[self.promptIndex % self.prompts.length];
        ph.classList.remove('is-leaving');
        ph.classList.add('is-arriving');
        requestAnimationFrame(function () { requestAnimationFrame(function () { ph.classList.remove('is-arriving'); }); });
      }, 400);
    }, 3200);
  };

  DateField.prototype.inRange = function (y, m, d) {
    var v = y * 10000 + m * 100 + d;
    return v >= this.min.y * 10000 + this.min.m * 100 + this.min.d && v <= this.max.y * 10000 + this.max.m * 100 + this.max.d;
  };

  DateField.prototype.set = function (value, silent) {
    var p = parse(value);
    if (!p || !this.inRange(p.y, p.m, p.d)) return;
    this.value = value;
    this.el.setAttribute('data-value', value);
    this.valueEl.textContent = formatDate(value);
    if (this.native) this.native.value = value;
    if (this.hidden) this.hidden.value = value;
    if (!silent) {
      this.el.dispatchEvent(new CustomEvent('pp:datechange', { bubbles: true, detail: { value: value } }));
    }
  };

  DateField.prototype.setView = function (y, m, keepFocus) {
    y = Math.max(this.min.y, Math.min(this.max.y, y));
    this.view = { y: y, m: m };
    this.monthEl.textContent = MONTHS[m - 1] + ' ' + y;
    var sel = parse(this.value);
    var today = todayISO();
    var first = new Date(Date.UTC(y, m - 1, 1)).getUTCDay();
    var lead = (first + 6) % 7;
    var html = '';
    for (var i = 0; i < 7; i++) html += '<span class="pp-cal__dow" aria-hidden="true">' + DOW[i] + '</span>';
    for (var j = 0; j < lead; j++) html += '<span></span>';
    var n = daysIn(y, m);
    var fd = this.focusDay && this.focusDay.y === y && this.focusDay.m === m ? this.focusDay.d :
      (sel && sel.y === y && sel.m === m ? sel.d : 1);
    fd = Math.min(fd, n);
    for (var d = 1; d <= n; d++) {
      var v = iso(y, m, d);
      var ok = this.inRange(y, m, d);
      html += '<button type="button" class="pp-cal__day' + (v === today ? ' is-today' : '') + '"' +
        ' data-date="' + v + '"' + (ok ? '' : ' disabled') +
        ' aria-label="' + d + ' ' + MONTHS[m - 1] + ' ' + y + '"' +
        ' aria-selected="' + (sel && v === this.value ? 'true' : 'false') + '"' +
        ' tabindex="' + (d === fd ? '0' : '-1') + '">' + d + '</button>';
    }
    this.gridEl.innerHTML = html;
    this.focusDay = { y: y, m: m, d: fd };
    var years = this.yearsEl.querySelectorAll('[data-year]');
    for (var k = 0; k < years.length; k++) {
      years[k].setAttribute('aria-selected', +years[k].getAttribute('data-year') === y ? 'true' : 'false');
    }
    var cur = this.yearsEl.querySelector('[aria-selected="true"]');
    if (cur) this.yearsEl.scrollTop = cur.offsetTop - this.yearsEl.clientHeight / 2 + cur.offsetHeight / 2;
    if (keepFocus === 'day') this.focusCurrent();
  };

  DateField.prototype.focusCurrent = function () {
    var b = this.gridEl.querySelector('[tabindex="0"]');
    if (b) b.focus();
  };

  DateField.prototype.stepMonth = function (step) {
    var m = this.view.m + step, y = this.view.y;
    if (m < 1) { m = 12; y--; }
    if (m > 12) { m = 1; y++; }
    if (y < this.min.y || y > this.max.y) return;
    this.setView(y, m);
  };

  DateField.prototype.gridKey = function (e) {
    var f = this.focusDay;
    if (!f) return;
    var date = new Date(Date.UTC(f.y, f.m - 1, f.d));
    var moved = true;
    switch (e.key) {
      case 'ArrowLeft': date.setUTCDate(date.getUTCDate() - 1); break;
      case 'ArrowRight': date.setUTCDate(date.getUTCDate() + 1); break;
      case 'ArrowUp': date.setUTCDate(date.getUTCDate() - 7); break;
      case 'ArrowDown': date.setUTCDate(date.getUTCDate() + 7); break;
      case 'PageUp': date.setUTCMonth(date.getUTCMonth() - (e.shiftKey ? 12 : 1)); break;
      case 'PageDown': date.setUTCMonth(date.getUTCMonth() + (e.shiftKey ? 12 : 1)); break;
      case 'Home': date.setUTCDate(date.getUTCDate() - ((date.getUTCDay() + 6) % 7)); break;
      case 'End': date.setUTCDate(date.getUTCDate() + (6 - (date.getUTCDay() + 6) % 7)); break;
      default: moved = false;
    }
    if (!moved) return;
    e.preventDefault();
    var y = date.getUTCFullYear(), m = date.getUTCMonth() + 1, d = date.getUTCDate();
    if (!this.inRange(y, m, d)) return;
    this.focusDay = { y: y, m: m, d: d };
    this.setView(y, m, 'day');
  };

  DateField.prototype.open = function () {
    if (this.el.classList.contains('is-open')) return;
    var p = parse(this.value) || parse(this.el.getAttribute('data-start') || '') || parse(todayISO());
    this.focusDay = p;
    this.cal.hidden = false;
    this.setView(p.y, p.m);
    this.place();
    this.el.classList.add('is-open');
    this.control.setAttribute('aria-expanded', 'true');
    var self = this;
    setTimeout(function () { self.focusCurrent(); }, 30);
  };

  /* Open on the side with room for the whole calendar. If neither side has
     room, open on the roomier side and scroll the page just enough that every
     day can be reached (it used to open downward off the bottom of the screen
     whenever the field sat in the upper half). */
  DateField.prototype.place = function () {
    var cal = this.cal;
    cal.classList.remove('pp-cal--up');
    var r = this.el.getBoundingClientRect();
    var h = cal.offsetHeight || 380;
    var top = 76;                                   /* the sticky header */
    var below = window.innerHeight - r.bottom - 16;
    var above = r.top - top - 16;
    var up = below < h && above > below;
    cal.classList.toggle('pp-cal--up', up);
    var short = (up ? above : below) - h;
    if (short < 0) {
      var by = up ? short - 8 : -short + 8;
      try { window.scrollBy({ top: by, behavior: reduceMotion.matches ? 'auto' : 'smooth' }); }
      catch (_) { window.scrollBy(0, by); }
    }
  };

  DateField.prototype.close = function (refocus) {
    if (!this.el.classList.contains('is-open')) return;
    this.el.classList.remove('is-open');
    this.control.setAttribute('aria-expanded', 'false');
    var cal = this.cal;
    setTimeout(function () { cal.hidden = true; }, 220);
    if (refocus) this.control.focus();
  };

  DateField.prototype.toggle = function () {
    if (this.el.classList.contains('is-open')) this.close(true);
    else this.open();
  };

  function initDateFields(scope) {
    (scope || doc).querySelectorAll('[data-pp-datefield]').forEach(function (el) {
      if (!el.ppField) el.ppField = new DateField(el);
    });
  }
  PP.initDateFields = initDateFields;

  /* Chips that set a sibling Date Field: data-pp-chip="today|random|YYYY-MM-DD" */
  doc.addEventListener('click', function (e) {
    var chip = e.target.closest('[data-pp-chip]');
    if (!chip) return;
    var scope = chip.closest('[data-pp-datescope]') || chip.parentElement;
    var field = scope.querySelector('[data-pp-datefield]');
    if (!field || !field.ppField) return;
    var v = chip.getAttribute('data-pp-chip');
    if (v === 'today') v = todayISO();
    else if (v === 'random') v = randomDate();
    field.ppField.set(v);
  });

  /* ---- video pause controls and film modal ---------------------------------- */
  function initMedia() {
    doc.querySelectorAll('video[data-pp-autoplay]').forEach(function (v) {
      var btn = (v.closest('[data-pp-media]') || v.parentElement).querySelector('[data-pp-pause]');
      if (reduceMotion.matches) {
        v.removeAttribute('autoplay');
        v.pause();
        if (btn) btn.classList.add('is-paused');
      } else if ('IntersectionObserver' in window) {
        new IntersectionObserver(function (es) {
          es.forEach(function (e) {
            if (btn && btn.classList.contains('is-paused')) return;
            /* a homepage slide that is not showing keeps its film still, and a slide's film plays once
               per showing and stays still while the slides are paused (pp-slides.js) */
            if (e.isIntersecting && !v.closest('[data-pp-slide-off]') && !v.ppStill && !(v.ended && v.ppSlides)) { var p = v.play(); if (p && p.catch) p.catch(function () {}); }
            else v.pause();
          });
        }, { threshold: 0.2 }).observe(v);
      }
    });
    doc.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-pp-pause]');
      if (btn) {
        var v = (btn.closest('[data-pp-media]') || btn.parentElement).querySelector('video');
        if (!v) return;
        if (v.paused) { v.play(); btn.classList.remove('is-paused'); btn.setAttribute('aria-label', 'Pause the film'); }
        else { v.pause(); btn.classList.add('is-paused'); btn.setAttribute('aria-label', 'Play the film'); }
        return;
      }
      var opener = e.target.closest('[data-pp-modal-open]');
      if (opener) {
        var modal = doc.getElementById(opener.getAttribute('data-pp-modal-open'));
        if (!modal) return;
        e.preventDefault();
        modal.hidden = false;
        requestAnimationFrame(function () { modal.classList.add('is-open'); });
        var mv = modal.querySelector('video');
        if (mv) {
          if (!mv.src && mv.getAttribute('data-src')) mv.src = mv.getAttribute('data-src');
          mv.currentTime = 0;
          var pr = mv.play(); if (pr && pr.catch) pr.catch(function () {});
        }
        modal.ppOpener = opener;
        var c = modal.querySelector('[data-pp-modal-close]');
        if (c) setTimeout(function () { c.focus(); }, 50);
        return;
      }
      var closer = e.target.closest('[data-pp-modal-close]');
      if (closer || e.target.classList.contains('pp-modal')) {
        var m = closer ? closer.closest('.pp-modal') : e.target;
        closeModal(m);
      }
    });
    doc.addEventListener('keydown', function (e) {
      if (e.key !== 'Escape') return;
      var m = doc.querySelector('.pp-modal.is-open');
      if (m) closeModal(m);
    });
    function closeModal(m) {
      if (!m) return;
      m.classList.remove('is-open');
      var mv = m.querySelector('video');
      if (mv) mv.pause();
      setTimeout(function () { m.hidden = true; }, 300);
      if (m.ppOpener) m.ppOpener.focus();
    }
  }

  /* ---- rails: progress line ---------------------------------------------------- */
  function initRails(scope) {
    (scope || doc).querySelectorAll('[data-pp-rail]').forEach(function (rail) {
      if (rail.ppRail) return;
      rail.ppRail = true;
      var host = rail.parentElement;
      var bar = host.querySelector('.pp-progress__bar');
      if (!bar) return;
      var track = bar.parentElement;
      var update = function () {
        var sw = rail.scrollWidth, cw = rail.clientWidth;
        var frac = sw > 0 ? Math.min(1, cw / sw) : 1;
        var tw = track.clientWidth;
        var x = sw > cw ? (rail.scrollLeft / (sw - cw)) * (tw - tw * frac) : 0;
        track.style.setProperty('--pp-progress-w', (frac * 100) + '%');
        track.style.setProperty('--pp-progress-x', x + 'px');
        track.hidden = frac >= 0.999;
      };
      rail.addEventListener('scroll', function () { requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }
  PP.initRails = initRails;

  /* ---- boot ------------------------------------------------------------------- */
  function boot() {
    initHeader();
    initSheets();
    initSearch();
    initDateFields();
    initMedia();
    initRails();
    observeReveals();
    doc.dispatchEvent(new CustomEvent('pp:ready'));
  }

  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', boot);
  else boot();

  doc.addEventListener('shopify:section:load', function (e) {
    initDateFields(e.target);
    initRails(e.target);
    observeReveals(e.target);
  });
})();
